import { Outlet } from "@tanstack/react-router"
import { Input } from "design-system/atoms/Input"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import { Section } from "design-system/molecules/Section"
import { Tabs, TabsList, TabsTrigger } from "design-system/molecules/Tabs"
import { isFilled } from "lopermut"
import { Package, Search, Store } from "lucide-react"
import { useCapabilitiesDictionary } from "modules/capabilities/hooks/useCapabilitiesDictionary"
import { useCapabilitiesQuery } from "modules/capabilities/hooks/useCapabilitiesQuery"
import { useMarketplaceCapabilitiesQuery } from "modules/capabilities/hooks/useMarketplaceCapabilitiesQuery"
import { Loading } from "modules/shared/components/Loading"
import { useMemo, useState } from "react"
import { Trans, useTranslation } from "react-i18next"
import { CustomCapabilityButton } from "../CreateCustomCapability/CustomCapabilityButton"
import { CapabilitiesListContent } from "./Content"

type CapabilitiesListProps = {
  tab: "installed" | "marketplace"
  onChangeTab: (tab: "installed" | "marketplace") => void
}

export function CapabilitiesList({ tab, onChangeTab: onChangeTabProp }: CapabilitiesListProps) {
  const { t } = useTranslation()

  const { data: installedCapabilities, isPending: isPendingInstalledCapabilities } =
    useCapabilitiesQuery({ enabled: tab === "installed" })
  const { data: marketplaceCapabilities, isPending: isPendingMarketplaceCapabilities } =
    useMarketplaceCapabilitiesQuery({ enabled: tab === "marketplace" })

  const capabilitiesDictionary = useCapabilitiesDictionary()
  const [search, setSearch] = useState("")

  function onChangeTab(tab: "installed" | "marketplace") {
    onChangeTabProp(tab)
  }

  const tabs = [
    {
      value: "installed",
      label: t("capabilities.tabs.installed"),
      icon: Package,
    },
    {
      value: "marketplace",
      label: t("capabilities.tabs.marketplace"),
      icon: Store,
    },
  ]

  const filteredCapabilities = useMemo(() => {
    switch (tab) {
      case "installed":
        let filteredInstalledCapabilities = [...(installedCapabilities ?? [])]
        if (isFilled(search)) {
          filteredInstalledCapabilities = filteredInstalledCapabilities.filter(({ identifier }) => {
            const { name, description, detailedDescription } =
              capabilitiesDictionary[identifier as keyof typeof capabilitiesDictionary]

            return (
              name.toLowerCase().includes(search.toLowerCase()) ||
              description.toLowerCase().includes(search.toLowerCase()) ||
              detailedDescription?.toLowerCase().includes(search.toLowerCase())
            )
          })
        }
        return filteredInstalledCapabilities
      case "marketplace":
        let filteredMarketplaceCapabilities = [...(marketplaceCapabilities ?? [])]
        if (isFilled(search)) {
          filteredMarketplaceCapabilities = filteredMarketplaceCapabilities.filter(
            ({ identifier }) => {
              const { name, description, detailedDescription } =
                capabilitiesDictionary[identifier as keyof typeof capabilitiesDictionary]

              return (
                name.toLowerCase().includes(search.toLowerCase()) ||
                description.toLowerCase().includes(search.toLowerCase()) ||
                detailedDescription?.toLowerCase().includes(search.toLowerCase())
              )
            },
          )
        }
        return filteredMarketplaceCapabilities
    }
  }, [installedCapabilities, marketplaceCapabilities, tab, search, capabilitiesDictionary])

  function renderContent() {
    if (tab === "installed" && isPendingInstalledCapabilities) return <Loading />
    if (tab === "marketplace" && isPendingMarketplaceCapabilities) return <Loading />

    return (
      <CapabilitiesListContent
        capabilities={filteredCapabilities}
        tab={tab}
        onChangeTab={onChangeTab}
        search={search}
      />
    )
  }

  return (
    <>
      <Section
        title={t("capabilities.list.title")}
        description={
          <Trans
            i18nKey="capabilities.list.description"
            components={{
              tooltip: (
                <Tooltip>
                  <TooltipTrigger slick>{t("capabilities.tooltip.title")}</TooltipTrigger>
                  <TooltipContent>{t("capabilities.tooltip.description")}</TooltipContent>
                </Tooltip>
              ),
            }}
          />
        }
        action={<CustomCapabilityButton />}
      >
        <Tabs
          value={tab}
          onValueChange={(value) => onChangeTab(value as "installed" | "marketplace")}
          className="flex flex-col gap-4"
        >
          <TabsList>
            {tabs.map(({ icon: Icon, label, value }) => (
              <TabsTrigger key={value} value={value}>
                <Icon className="size-4" />
                <span>{label}</span>
              </TabsTrigger>
            ))}
          </TabsList>
          <div className="flex flex-col gap-4">
            <Input
              icon={Search}
              placeholder={t("capabilities.list.search.placeholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {renderContent()}
          </div>
        </Tabs>
      </Section>
      <Outlet />
    </>
  )
}
